import { cva, type VariantProps } from "cva";
import { CircleAlert, type LucideIcon } from "lucide-react";
import { forwardRef, useRef, type HTMLAttributes } from "react";
import { useFocusable } from "react-aria";
import type {
  FieldErrorProps,
  GroupProps,
  InputProps,
  LabelProps,
  TextProps,
} from "react-aria-components";
import {
  Group,
  FieldError as RACFieldError,
  Input as RACInput,
  Label as RACLabel,
  Text,
  composeRenderProps,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Tooltip } from "./Tooltip";
import { composeTailwindRenderProps } from "./utils";

interface InputPropsWithIcon extends Omit<InputProps, "size"> {
  icon?: LucideIcon;
  size?: "sm" | "md";
}

export interface FieldProps
  extends InputPropsWithIcon,
    Omit<VariantProps<typeof fieldStyles>, "suffix"> {
  showErrorIcon?: boolean;
  errorIconTooltip?: React.ReactNode;
  suffix?: React.ReactNode;
  wrapperClassName?: string;
}

export function Label(props: LabelProps) {
  return (
    <RACLabel
      {...props}
      className={twMerge(
        "text-theme-on-background-variant w-fit cursor-default text-sm font-medium",
        props.className,
      )}
    />
  );
}

export function Description(props: TextProps) {
  return (
    <Text
      {...props}
      slot="description"
      className={twMerge(
        "text-theme-on-background-variant text-sm",
        props.className,
      )}
    />
  );
}

export function FieldError(props: FieldErrorProps) {
  return (
    <RACFieldError
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "text-[11px] text-red-600",
      )}
    />
  );
}

const fieldWrapperStyles = cva({
  base: "relative flex",
  variants: {
    theme: {
      default: "",
      stacked: "h-20 -my-7 -mx-5",
      none: "",
    },
  },
  defaultVariants: {
    theme: "default",
  },
});

const fieldStyles = cva({
  base: "w-full grow-0 shrink text-sm font-bold placeholder:font-medium transition-[color,border-color] duration-200",
  variants: {
    theme: {
      default:
        "rounded-sm border border-theme-outline-variant text-theme-on-background outline-0 placeholder:text-theme-on-background-variant",
      stacked:
        "input h-full px-8 text-theme-on-background placeholder:text-theme-on-background-variant",
      none: "input p-0",
    },
    suffix: {
      true: "pr-16!",
      false: null,
    },
    showIcon: {
      true: null,
      false: null,
    },
    showErrorIcon: {
      true: null,
      false: null,
    },
    size: {
      sm: "p-4",
      md: "p-7",
    },
  },
  compoundVariants: [
    {
      showIcon: true,
      theme: "default",
      className: "pl-16",
    },
    {
      showErrorIcon: true,
      theme: "default",
      className: "pr-16",
    },
    {
      showIcon: true,
      theme: "stacked",
      className: "pl-17",
    },
    {
      showErrorIcon: true,
      theme: "stacked",
      className: "pr-17",
    },
  ],
  defaultVariants: {
    theme: "default",
    showErrorIcon: false,
    size: "md",
  },
});

const fieldGroupStyles = cva({
  base: "",
  variants: {},
});

export function FieldGroup(props: GroupProps) {
  return (
    <Group
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        fieldGroupStyles({ ...renderProps, className }),
      )}
    />
  );
}

export const FieldErrorIcon = (props: HTMLAttributes<HTMLDivElement>) => {
  const ref = useRef<HTMLDivElement>(null);

  const { focusableProps } = useFocusable(props, ref);

  return (
    <div
      {...focusableProps}
      ref={ref}
      className={twMerge(
        "rounded-full outline-0 outline-offset-4 outline-blue-500 focus-visible:outline-2",
        props.className,
      )}
      tabIndex={0}
    >
      <CircleAlert className="size-5 text-red-600" />
    </div>
  );
};

export const Input = forwardRef<HTMLInputElement, FieldProps>(
  (
    { showErrorIcon, errorIconTooltip, size, wrapperClassName, ...props },
    ref,
  ) => {
    const [iconLeft, iconRight] =
      props.theme === "stacked"
        ? ["left-8", "right-8"]
        : props.theme === "none"
          ? ["left-0", "right-0"]
          : ["left-7", "right-7"];

    return (
      <div
        className={twMerge(
          fieldWrapperStyles({ theme: props.theme }),
          wrapperClassName,
        )}
      >
        {props.icon && (
          <props.icon
            className={`absolute top-[calc(50%-10px)] ${iconLeft} text-theme-on-background size-5`}
          />
        )}

        <RACInput
          {...props}
          ref={ref}
          className={composeTailwindRenderProps(
            props.className,
            fieldStyles({
              theme: props.theme,
              showIcon: !!props.icon,
              suffix: !!props.suffix,
              showErrorIcon,
              size,
            }),
          )}
        />
        {props.suffix && (
          <div className="absolute top-[calc(50%-10px)] right-7">
            {props.suffix}
          </div>
        )}

        {showErrorIcon && (
          <Tooltip
            tooltipTriggerProps={{ delay: 0 }}
            placement="left"
            tooltipText={errorIconTooltip}
            className="flex flex-col"
          >
            <FieldErrorIcon
              className={`absolute top-[calc(50%-10px)] ${iconRight}`}
            />
          </Tooltip>
        )}
      </div>
    );
  },
);
